import React, { useRef, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Grid, Text, Line } from '@react-three/drei';
import * as THREE from 'three';
import { PlateCalculationInput, CalculationResult } from './StrengthCalculator';
import { MATERIALS } from './MaterialDatabase';

interface StrengthVisualization3DProps {
  input: PlateCalculationInput;
  result: CalculationResult;
}

// Komponent płyty z deformacją
const DeformedPlate: React.FC<{
  width: number;
  length: number;
  thickness: number;
  deflectionGrid: number[][];
  material: string;
  maxStress: number;
}> = ({ width, length, thickness, deflectionGrid, material, maxStress }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const geometryRef = useRef<THREE.PlaneGeometry>(null);

  useEffect(() => {
    if (!geometryRef.current || !deflectionGrid || !Array.isArray(deflectionGrid) || deflectionGrid.length === 0) {
      console.warn('[StrengthVisualization3D] DeflectionGrid is not valid:', deflectionGrid);
      return;
    }

    const geometry = geometryRef.current;
    const positions = geometry.attributes.position;
    
    // Skalowanie do jednostek 3D (mm -> jednostki sceny)
    const scale = 0.01; // 1mm = 0.01 jednostki
    
    // Aplikuj deformację do wierzchołków
    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      
      // Sprawdź czy deflectionGrid jest poprawny
      if (!deflectionGrid || deflectionGrid.length === 0 || !deflectionGrid[0]) {
        positions.setZ(i, 0);
        continue;
      }
      
      // Mapuj pozycję na siatkę ugięcia
      const gridX = Math.floor((x / (width * scale) + 0.5) * (deflectionGrid[0].length - 1));
      const gridY = Math.floor((y / (length * scale) + 0.5) * (deflectionGrid.length - 1));
      
      if (deflectionGrid[gridY] && deflectionGrid[gridY][gridX] !== undefined) {
        const z = deflectionGrid[gridY][gridX] * scale * 10; // Przeskaluj ugięcie dla lepszej widoczności
        positions.setZ(i, z);
      }
    }
    
    positions.needsUpdate = true;
    geometry.computeVertexNormals();
  }, [deflectionGrid, width, length]);

  // Kolory w zależności od naprężeń
  const materialProps = MATERIALS[material];
  const stressRatio = maxStress / materialProps.flexuralStrength;
  
  const getColorFromStress = (ratio: number): string => {
    if (ratio < 0.3) return '#00ff00'; // Zielony - bezpieczne
    if (ratio < 0.6) return '#ffff00'; // Żółty - średnie
    if (ratio < 0.8) return '#ff8800'; // Pomarańczowy - wysokie
    return '#ff0000'; // Czerwony - niebezpieczne
  };

  return (
    <mesh ref={meshRef} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry 
        ref={geometryRef}
        args={[width * 0.01, length * 0.01, 20, 20]} 
      />
      <meshPhysicalMaterial
        color={getColorFromStress(stressRatio)}
        metalness={0.2}
        roughness={0.5}
        transparent
        opacity={0.8}
        side={THREE.DoubleSide}
        wireframe={false}
      />
    </mesh>
  );
};

// Komponenty pomocnicze dla wizualizacji
const SupportIndicator: React.FC<{
  type: string;
  width: number;
  length: number;
}> = ({ type, width, length }) => {
  const scale = 0.01;
  const w = width * scale;
  const l = length * scale;
  const supportSize = 0.2;
  
  const supports = [];
  
  switch (type) {
    case 'simply_supported':
      // Podpory na dwóch końcach
      supports.push(
        <mesh key="support1" position={[-w/2, -0.1, 0]}>
          <cylinderGeometry args={[supportSize, supportSize, 0.2]} />
          <meshStandardMaterial color="#888888" />
        </mesh>,
        <mesh key="support2" position={[w/2, -0.1, 0]}>
          <cylinderGeometry args={[supportSize, supportSize, 0.2]} />
          <meshStandardMaterial color="#888888" />
        </mesh>
      );
      break;
      
    case 'four_sides':
      // Ramka dookoła
      const frameThickness = 0.05;
      supports.push(
        <group key="frame">
          {/* Górna krawędź */}
          <mesh position={[0, 0, l/2]}>
            <boxGeometry args={[w, frameThickness, frameThickness]} />
            <meshStandardMaterial color="#666666" />
          </mesh>
          {/* Dolna krawędź */}
          <mesh position={[0, 0, -l/2]}>
            <boxGeometry args={[w, frameThickness, frameThickness]} />
            <meshStandardMaterial color="#666666" />
          </mesh>
          {/* Lewa krawędź */}
          <mesh position={[-w/2, 0, 0]}>
            <boxGeometry args={[frameThickness, frameThickness, l]} />
            <meshStandardMaterial color="#666666" />
          </mesh>
          {/* Prawa krawędź */}
          <mesh position={[w/2, 0, 0]}>
            <boxGeometry args={[frameThickness, frameThickness, l]} />
            <meshStandardMaterial color="#666666" />
          </mesh>
        </group>
      );
      break;
      
    case 'cantilever':
      // Zamocowanie z jednej strony
      supports.push(
        <mesh key="fixed" position={[-w/2, 0, 0]}>
          <boxGeometry args={[0.1, 0.3, l * scale]} />
          <meshStandardMaterial color="#444444" />
        </mesh>
      );
      break;
  }
  
  return <>{supports}</>;
};

// Wskaźnik obciążenia
const LoadIndicator: React.FC<{
  type: 'uniform' | 'point';
  value: number;
  width: number;
  length: number;
  position?: { x: number; y: number };
}> = ({ type, value, width, length, position }) => {
  const scale = 0.01;
  
  if (type === 'uniform') {
    // Strzałki pokazujące obciążenie równomierne
    const arrows = [];
    const gridSize = 5;
    
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const x = ((i / (gridSize - 1)) - 0.5) * width * scale;
        const z = ((j / (gridSize - 1)) - 0.5) * length * scale;
        
        arrows.push(
          <group key={`arrow-${i}-${j}`} position={[x, 0.5, z]}>
            <mesh rotation={[Math.PI, 0, 0]}>
              <coneGeometry args={[0.05, 0.2, 8]} />
              <meshStandardMaterial color="#ff6600" />
            </mesh>
            <mesh position={[0, 0.2, 0]}>
              <cylinderGeometry args={[0.02, 0.02, 0.4]} />
              <meshStandardMaterial color="#ff6600" />
            </mesh>
          </group>
        );
      }
    }
    
    return (
      <>
        {arrows}
        <Text
          position={[0, 0.8, 0]}
          fontSize={0.15}
          color="#ffffff"
          anchorX="center"
          anchorY="middle"
        >
          {value} kg/m²
        </Text>
      </>
    );
  } else {
    // Pojedyncza strzałka dla obciążenia punktowego
    const x = position ? position.x * scale : 0;
    const z = position ? position.y * scale : 0;
    
    return (
      <group position={[x, 0.5, z]}>
        <mesh rotation={[Math.PI, 0, 0]}>
          <coneGeometry args={[0.1, 0.3, 8]} />
          <meshStandardMaterial color="#ff0000" />
        </mesh>
        <mesh position={[0, 0.25, 0]}>
          <cylinderGeometry args={[0.04, 0.04, 0.5]} />
          <meshStandardMaterial color="#ff0000" />
        </mesh>
        <Text
          position={[0, 0.6, 0]}
          fontSize={0.15}
          color="#ffffff"
          anchorX="center"
          anchorY="middle"
        >
          {value} kg
        </Text>
      </group>
    );
  }
};

// Legenda kolorów
const ColorLegend: React.FC<{ maxStress: number; material: string }> = ({ maxStress, material }) => {
  const mat = MATERIALS[material];
  const levels = [
    { ratio: 0, color: '#00ff00', label: '0%' },
    { ratio: 0.3, color: '#ffff00', label: '30%' },
    { ratio: 0.6, color: '#ff8800', label: '60%' },
    { ratio: 0.8, color: '#ff0000', label: '80%' },
    { ratio: 1, color: '#990000', label: '100%' }
  ];
  
  return (
    <group position={[3, 2, 0]}>
      <Text
        position={[0, 0.5, 0]}
        fontSize={0.12}
        color="#ffffff"
        anchorX="center"
      >
        Naprężenia
      </Text>
      {levels.map((level, idx) => (
        <group key={idx} position={[0, -idx * 0.3, 0]}>
          <mesh position={[-0.3, 0, 0]}>
            <boxGeometry args={[0.2, 0.2, 0.01]} />
            <meshStandardMaterial color={level.color} />
          </mesh>
          <Text
            position={[0.2, 0, 0]}
            fontSize={0.1}
            color="#ffffff"
            anchorX="left"
          >
            {level.label} ({(level.ratio * mat.flexuralStrength).toFixed(0)} MPa)
          </Text>
        </group>
      ))}
    </group>
  );
};

// Główny komponent wizualizacji
export const StrengthVisualization3D: React.FC<StrengthVisualization3DProps> = ({
  input,
  result
}) => {
  return (
    <Canvas
      camera={{ position: [5, 5, 5], fov: 50 }}
      gl={{ antialias: true }}
    >
      {/* Oświetlenie */}
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 10, 5]} intensity={1} castShadow />
      <pointLight position={[-10, 5, -5]} intensity={0.5} />
      
      {/* Kontrolki */}
      <OrbitControls 
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        minDistance={2}
        maxDistance={20}
      />
      
      {/* Siatka pomocnicza */}
      <Grid
        args={[10, 10]}
        cellSize={0.5}
        cellThickness={0.5}
        cellColor="#4a5568"
        sectionSize={2.5}
        sectionThickness={1}
        sectionColor="#718096"
        fadeDistance={20}
        fadeStrength={1}
        followCamera={false}
      />
      
      {/* Deformowana płyta */}
      <DeformedPlate
        width={input.width}
        length={input.length}
        thickness={input.thickness}
        deflectionGrid={result.visualizationData?.deflectionGrid || [[0]]}
        material={input.material}
        maxStress={result.maxStress}
      />
      
      {/* Wskaźniki podparcia */}
      <SupportIndicator
        type={input.supportType}
        width={input.width}
        length={input.length}
      />
      
      {/* Wskaźnik obciążenia */}
      <LoadIndicator
        type={input.loadType}
        value={input.load}
        width={input.width}
        length={input.length}
        position={input.loadPosition}
      />
      
      {/* Legenda kolorów */}
      <ColorLegend
        maxStress={result.maxStress}
        material={input.material}
      />
      
      {/* Informacje tekstowe */}
      <group position={[-3, 2, 0]}>
        <Text
          position={[0, 0.5, 0]}
          fontSize={0.12}
          color="#ffffff"
          anchorX="center"
        >
          Wyniki
        </Text>
        <Text
          position={[0, 0.2, 0]}
          fontSize={0.1}
          color="#ffff00"
          anchorX="center"
        >
          Ugięcie: {result.maxDeflection.toFixed(2)} mm
        </Text>
        <Text
          position={[0, -0.1, 0]}
          fontSize={0.1}
          color={result.isSafe ? '#00ff00' : '#ff0000'}
          anchorX="center"
        >
          {result.isSafe ? 'BEZPIECZNE' : 'NIEBEZPIECZNE'}
        </Text>
        <Text
          position={[0, -0.4, 0]}
          fontSize={0.08}
          color="#aaaaaa"
          anchorX="center"
        >
          {result.percentOfMaxStress}% wytrzymałości
        </Text>
      </group>
      
      {/* Osie współrzędnych */}
      <group position={[0, 0, 0]}>
        {/* Oś X - czerwona */}
        <Line
          points={[[0, 0, 0], [1, 0, 0]]}
          color="red"
          lineWidth={2}
        />
        <Text
          position={[1.2, 0, 0]}
          fontSize={0.1}
          color="red"
        >
          X
        </Text>
        
        {/* Oś Y - zielona */}
        <Line
          points={[[0, 0, 0], [0, 1, 0]]}
          color="green"
          lineWidth={2}
        />
        <Text
          position={[0, 1.2, 0]}
          fontSize={0.1}
          color="green"
        >
          Y
        </Text>
        
        {/* Oś Z - niebieska */}
        <Line
          points={[[0, 0, 0], [0, 0, 1]]}
          color="blue"
          lineWidth={2}
        />
        <Text
          position={[0, 0, 1.2]}
          fontSize={0.1}
          color="blue"
        >
          Z
        </Text>
      </group>
    </Canvas>
  );
};